import { useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
  faCommentPlus,
  faEye,
  faEyeSlash,
} from "@fortawesome/pro-regular-svg-icons";
import CommentsList from "./CommentsList";
import { NewCommentForm } from ".";
import { UserBasicDetailsDto } from "../../types/dtos/generic";
import { ReactComponent as NoCommentsImage } from "../../images/noComments.svg";
import UserContext from "../../state/UserContext";
import { commentsHelper, userDetailsHelper } from "../../helpers";
import { NewCommentDto, SavedCommentDto } from "../../types/dtos/forms";
import { t } from "i18next";
import { AnswerSetCommentVisibility } from "../../types/collab-docs/AnswerSetCommentVisibility";
import { ApprovalFlow } from "../../types/collab-docs/ApprovalFlow";

interface CommentsSideBarProps {
  activeQuestionId: string | null;
  comments: SavedCommentDto[] | null;
  participants: UserBasicDetailsDto[];
  isReadOnly: boolean;
  isExpanded: boolean;
  allowCommentVisibilityModeChange: boolean;
  /** A method to call to mark the comments as seen */
  onCommentsSeen(questionId: string): void;
  /** A method to call to insert a new comment */
  onCommentAdd(
    newComment: NewCommentDto,
    successCallback: () => void,
    errorCallback: () => void,
  ): void;
  /** A method to call to delete a comment */
  onCommentDelete(
    commentId: string,
    successCallback: () => void,
    errorCallback: () => void,
  ): void;
  onExpandCollapse: (expanded: boolean) => void;

  /** The method to call when the visibility of a comment is changed. `Undefined` for viewer mode as comments are not editable in this mode. */
  onCommentVisibilityChange?(
    commentId: string | number,
    visibility: AnswerSetCommentVisibility,
  ): void;
  /** The approval flow type - affects whether private notes should be obscured */
  approvalFlow?: ApprovalFlow;
}

function CommentsSideBar({
  activeQuestionId,
  comments,
  participants,
  isReadOnly,
  isExpanded,
  allowCommentVisibilityModeChange,
  onCommentsSeen,
  onCommentAdd,
  onCommentDelete,
  onExpandCollapse,
  onCommentVisibilityChange = undefined,
  approvalFlow,
}: CommentsSideBarProps) {
  // Context
  const userContext = useContext(UserContext);

  // State
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [seenCommentsForQuestionIds, setSeenCommentsForQuestionIds] = useState<
    string[]
  >([]);
  const [showPrivateNotes, setShowPrivateNotes] = useState<boolean>(false);

  // Update showPrivateNotes when approvalFlow changes
  // For HIDDEN-TO-EMPLOYEE approval flow, show private notes by default (it's not a discussion)
  useEffect(() => {
    setShowPrivateNotes(approvalFlow === "HIDDEN-TO-EMPLOYEE");
  }, [approvalFlow]);

  // Refs
  const commentFormInputRef = useRef<HTMLTextAreaElement>(null);

  const otherParticipantName = userDetailsHelper.getOtherUsersDisplayName(
    userContext.user.id,
    participants,
  );

  const introText = otherParticipantName
    ? t("Comments.SideBar.IntroWithOtherParticipantName").replace(
        "#OTHER_PARTICIPANT#",
        otherParticipantName,
      )
    : t("Comments.SideBar.Intro");

  // Determine whether the sidebar is visible so we know whether or not to mark the
  // comments loaded in it as "seen" (as this component loads when the active question changes,
  // but is hidden with responsive css. So we can't just mark them as seen, as this will execute,
  // but the user may not have actually seen it)
  const sidebarElement = document.getElementById("comments-sidebar");
  let sidebarIsVisible = false;
  if (sidebarElement) {
    sidebarIsVisible =
      window.getComputedStyle(sidebarElement).display === "block";
  }

  // Events
  useEffect(() => {
    // Ensure `replyToCommentId` gets reset as the activeQuestion changes
    setReplyToCommentId(null);
    // Mark the comments as "seen" (unless on mobile, in which case mark as seen on comments modal load)
    const unseenCommentCount = commentsHelper.getUnseenCommentCount(
      comments,
      userContext.user.id,
    );
    if (sidebarIsVisible && unseenCommentCount > 0 && activeQuestionId) {
      onCommentsSeen(activeQuestionId);
    }

    // Set the log of the previous active question, which is used to toggle
    // display of the help text on the comments sidebar
    if (
      activeQuestionId !== null &&
      seenCommentsForQuestionIds.indexOf(activeQuestionId) === -1
    ) {
      setSeenCommentsForQuestionIds([
        ...seenCommentsForQuestionIds,
        activeQuestionId,
      ]);
    }
  }, [activeQuestionId]);

  /** When a user clicks the "Reply" button on another comment */
  const onInitiateReply = (commentId: string) => {
    setReplyToCommentId(commentId);
    // Focus the textarea
    if (commentFormInputRef?.current) {
      commentFormInputRef.current.focus();
    }
  };

  const toggleExpandCollapseSidebar = () => {
    onExpandCollapse(!isExpanded);
  };

  return (
    <div
      id="comments-sidebar"
      className="hidden lg:block lg:overflow-auto px-2"
    >
      {!activeQuestionId && (
        <div className="text-center text-gray-400">
          <NoCommentsImage width={"18rem"} className="block m-auto mb-2" />
          <div>{t("Comments.SideBar.NoQuestionsSelected")}</div>
          <div>{t("Comments.SideBar.SelectAQuestion")}</div>
        </div>
      )}
      {activeQuestionId && (
        <>
          <div className="flex">
            <h3 className="!mb-0 font-semibold text-slate-600 flex-grow">
              {t("Comments.SideBar.Title")}
              <FontAwesomeIcon
                icon={faCommentPlus}
                size="1x"
                className="pl-1"
              />
            </h3>
            <button
              className="flex-none w-5 text-right"
              title={isExpanded ? "Collapse" : "Expand"}
              onClick={toggleExpandCollapseSidebar}
            >
              <FontAwesomeIcon
                icon={
                  isExpanded
                    ? faArrowDownLeftAndArrowUpRightToCenter
                    : faArrowUpRightAndArrowDownLeftFromCenter
                }
                size="1x"
                className="text-slate-500"
              />
            </button>
          </div>
          <p className="text-sm mt-1 mb-3 text-slate-600">{introText}</p>
          {comments && comments.some((c) => c.visibility === "PRIVATE") && (
            <div className="mb-1 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowPrivateNotes(!showPrivateNotes)}
                className="inline-flex items-center gap-2 text-xs text-gray-600 hover:text-gray-800 transition-colors"
                title={
                  showPrivateNotes ? "Hide private notes" : "Show private notes"
                }
              >
                <FontAwesomeIcon
                  icon={showPrivateNotes ? faEye : faEyeSlash}
                  className="text-amber-600"
                />
                <span>{showPrivateNotes ? "Hide" : "Show"} Private Notes</span>
              </button>
            </div>
          )}
          <hr className="mb-4" />

          <CommentsList
            isReadOnly={isReadOnly}
            comments={comments}
            participants={participants}
            allowCommentVisibilityModeChange={allowCommentVisibilityModeChange}
            onDeleteComment={onCommentDelete}
            onTargetCommentForReply={onInitiateReply}
            onCommentVisibilityChange={onCommentVisibilityChange}
            showPrivateNotes={showPrivateNotes}
          />
          {!isReadOnly && (
            <div className="mt-4">
              <NewCommentForm
                questionId={activeQuestionId}
                replyToCommentId={replyToCommentId}
                onSubmit={onCommentAdd}
                inputRef={commentFormInputRef}
                allowCommentVisibilityModeChange={
                  allowCommentVisibilityModeChange
                }
                otherParticipantName={otherParticipantName}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CommentsSideBar;
